// src/components/settings/catalog/CatalogItemList.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 2 (+ Stage 4).
//
// The list: search box, type chips, the count line, the table (it scrolls sideways inside its own
// box on a phone, so the page never does), "Show more", and the empty / loading / error states.
// Rows come from useCatalogSearch (searchCatalogOffice, 50 at a time). Tapping a row opens it.
// Stage 4: a tick box per row and "select all shown" in the header; when every shown row is ticked
// and more match, "Select all M matching items"; a favorite star per row. The tick box and the
// star are their own 44px buttons — tapping them never opens the row.
import React from 'react';
import { Search, X, Loader2, Star } from 'lucide-react';
import { TYPE_CHIPS, countLine, formatMoney, formatPct } from '../../../utils/catalogUi';
import { BTN_GHOST, BTN_PRIMARY, BTN_LINK, ErrorLine, TypeBadge } from './catalogUiParts';

const stop = (e) => e.stopPropagation();

// Laptop widths (lg up to 1760px): next to the Settings menu and the folder column the list has only
// ~520px at a 1440px window, so the table goes compact — type + unit under the name, cost + markup
// under the price — instead of scrolling sideways. Phones and tablets keep all 9 columns (scrolling
// inside their box, as before); very wide screens have room for them again.
const WIDE_COL = 'lg:hidden min-[1760px]:table-cell';
const COMPACT_LINE = 'hidden lg:block min-[1760px]:hidden';

function TickBox({ checked, indeterminate, onChange, label }) {
    const ref = React.useRef(null);
    React.useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate; }, [indeterminate]);
    return (
        <label className="w-11 h-11 inline-flex items-center justify-center cursor-pointer" onClick={stop} onKeyDown={stop}>
            <input ref={ref} type="checkbox" className="w-5 h-5 accent-[#5F4EC9] cursor-pointer" checked={checked} onChange={onChange} aria-label={label} />
        </label>
    );
}

export default function CatalogItemList({
    search, query, onQuery, type, onType, archivedView, folderView, selectedId, onOpen, folderNames, onNewItem,
    ticked, allMatching, onTick, onTickShown, onSelectAllMatching, onClearSelection, onToggleFavorite, bulkBar,
}) {
    const filtered = !!(query && query.trim()) || !!type;
    const busy = search.loading || search.typingQuery;
    const line = countLine({
        count: search.count, countIsExact: search.countIsExact, shown: search.items.length,
        searching: busy || search.loadingMore || search.searching, filtered,
    });
    const shownIds = search.items.map((x) => x.id);
    const tickedShown = shownIds.filter((id) => ticked.has(id)).length;
    const allShown = shownIds.length > 0 && (allMatching || tickedShown === shownIds.length);
    const moreMatch = !!search.nextCursor || (search.countIsExact && typeof search.count === 'number' && search.count > shownIds.length) || (search.countIsExact === false);
    const matchLabel = search.countIsExact && typeof search.count === 'number'
        ? `Select all ${search.count.toLocaleString('en-US')} matching items`
        : `Select all matching items${typeof search.count === 'number' && search.count > 0 ? ` (${Math.max(search.count, shownIds.length).toLocaleString('en-US')}+)` : ''}`;

    return (
        <div className="space-y-3 min-w-0">
            <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <label htmlFor="catalog-search" className="sr-only">Search the catalog</label>
                <input id="catalog-search" type="search" value={query} onChange={(e) => onQuery(e.target.value)}
                    placeholder="Search name, part #, manufacturer, supplier part #, barcode…"
                    className="w-full min-h-[44px] pl-10 pr-12 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/30" />
                {query && (
                    <button type="button" className="absolute right-0 top-0 w-11 h-11 inline-flex items-center justify-center text-gray-400 hover:text-gray-700" aria-label="Clear the search" onClick={() => onQuery('')}>
                        <X size={16} />
                    </button>
                )}
            </div>

            <div className="flex flex-wrap gap-2" role="group" aria-label="Type">
                {TYPE_CHIPS.map((c) => {
                    const on = (type || null) === c.value;
                    return (
                        <button key={c.label} type="button" aria-pressed={on} onClick={() => onType(c.value)}
                            className={`min-h-[44px] px-4 rounded-full text-sm font-medium border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40 ${
                                on ? 'bg-[#5F4EC9] text-white border-[#5F4EC9]' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>
                            {c.label}
                        </button>
                    );
                })}
            </div>

            <div className="flex items-center gap-2 min-h-[20px] text-sm text-gray-600" aria-live="polite">
                {(busy || search.searching) && <Loader2 size={14} className="animate-spin text-gray-400" aria-hidden="true" />}
                <span>{line}</span>
            </div>

            {archivedView && (
                <p className="text-sm text-gray-600 p-3 rounded-lg bg-gray-50 border border-gray-200">
                    Archived items can't be added to new estimates or invoices, but old ones still show them.
                </p>
            )}

            {bulkBar}

            {allShown && !allMatching && moreMatch && (
                <div className="flex flex-wrap items-center gap-x-2 p-2 px-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700">
                    <span>All {shownIds.length.toLocaleString('en-US')} items shown are selected.</span>
                    <button type="button" className={BTN_LINK} onClick={onSelectAllMatching}>{matchLabel}</button>
                </div>
            )}
            {allMatching && (
                <div className="flex flex-wrap items-center gap-x-2 p-2 px-3 rounded-lg bg-[#F4F2FD] border border-[#5F4EC9]/30 text-sm text-gray-800">
                    <span>All matching items are selected{typeof search.count === 'number' ? ` (${search.count.toLocaleString('en-US')}${search.countIsExact ? '' : '+'})` : ''}.</span>
                    <button type="button" className={BTN_LINK} onClick={onClearSelection}>Clear selection</button>
                </div>
            )}

            {search.error && <ErrorLine text={search.error} onRetry={search.retry} />}

            {!search.error && search.loading && search.items.length === 0 && (
                <div className="py-12 text-center text-sm text-gray-500"><Loader2 size={20} className="inline animate-spin mr-2" />Loading your catalog…</div>
            )}

            {!search.loading && !search.error && search.items.length === 0 && !search.nextCursor && (
                <div className="py-10 px-4 text-center rounded-xl border border-dashed border-gray-300 bg-gray-50 space-y-3">
                    {filtered ? (
                        <>
                            <p className="text-sm text-gray-600">Nothing matches. Try fewer words, or clear the search.</p>
                            <button type="button" className={BTN_GHOST} onClick={() => { onQuery(''); onType(null); }}>Clear the search</button>
                        </>
                    ) : archivedView ? (
                        <p className="text-sm text-gray-600">No archived items.</p>
                    ) : folderView === 'favorites' ? (
                        <p className="text-sm text-gray-600">No favorites yet. Tap the star next to an item to add it here.</p>
                    ) : folderView ? (
                        <>
                            <p className="text-sm text-gray-600">No items here yet.</p>
                            <button type="button" className={BTN_PRIMARY} onClick={onNewItem}>+ New item</button>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-gray-600">Your catalog is empty.</p>
                            <button type="button" className={BTN_PRIMARY} onClick={onNewItem}>+ New item</button>
                        </>
                    )}
                </div>
            )}

            {search.items.length > 0 && (
                <div className={`border border-gray-200 rounded-xl overflow-x-auto ${busy ? 'opacity-60' : ''}`}>
                    <table className="w-full min-w-[820px] lg:min-w-[500px] min-[1760px]:min-w-[820px] text-sm">
                        <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            <tr>
                                <th scope="col" className="pl-1 w-12">
                                    <TickBox checked={allShown} indeterminate={!allShown && tickedShown > 0} label="Select all items shown"
                                        onChange={(e) => onTickShown(e.target.checked)} />
                                </th>
                                <th scope="col" className="w-12"><span className="sr-only">Favorite</span></th>
                                <th scope="col" className="px-3 py-3">Name</th>
                                <th scope="col" className={`px-3 py-3 ${WIDE_COL}`}>Type</th>
                                <th scope="col" className={`px-3 py-3 ${WIDE_COL}`}>Unit</th>
                                <th scope="col" className="px-3 py-3 text-right">Price</th>
                                <th scope="col" className={`px-3 py-3 text-right ${WIDE_COL}`}>Cost</th>
                                <th scope="col" className={`px-3 py-3 text-right ${WIDE_COL}`}>Markup</th>
                                <th scope="col" className="px-4 py-3">Supplier</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {search.items.map((it) => {
                                const folders = folderNames(it.folderIds);
                                const active = it.id === selectedId;
                                const isTicked = allMatching || ticked.has(it.id);
                                return (
                                    <tr key={it.id} tabIndex={0} role="button" aria-label={`Open ${it.name}`}
                                        onClick={() => onOpen(it.id)} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(it.id); } }}
                                        className={`cursor-pointer focus:outline-none focus-visible:bg-[#5F4EC9]/10 ${active ? 'bg-[#5F4EC9]/10' : isTicked ? 'bg-[#F4F2FD]' : 'hover:bg-gray-50'}`}>
                                        <td className="pl-1 align-top">
                                            <TickBox checked={isTicked} label={`Select ${it.name}`} onChange={() => onTick(it.id)} />
                                        </td>
                                        <td className="align-top">
                                            <button type="button" aria-pressed={it.favorite === true} onClick={(e) => { stop(e); onToggleFavorite(it); }} onKeyDown={stop}
                                                aria-label={it.favorite ? `Remove ${it.name} from favorites` : `Add ${it.name} to favorites`}
                                                className="w-11 h-11 inline-flex items-center justify-center rounded-lg hover:bg-amber-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40">
                                                <Star size={18} className={it.favorite ? 'fill-amber-400 text-amber-500' : 'text-gray-300'} />
                                            </button>
                                        </td>
                                        <td className="px-3 py-3 align-top">
                                            <div className="font-medium text-gray-900">{it.name || '(no name)'}</div>
                                            <div className="text-xs text-gray-400 mt-0.5">
                                                {folders || 'Ungrouped'}{it.partNumber ? ` · Part # ${it.partNumber}` : ''}
                                            </div>
                                            <div className={`${COMPACT_LINE} mt-1 text-xs text-gray-500`}>
                                                <TypeBadge type={it.type} />{it.unit ? <span className="ml-1.5">{it.unit}</span> : null}
                                            </div>
                                        </td>
                                        <td className={`px-3 py-3 align-top ${WIDE_COL}`}><TypeBadge type={it.type} /></td>
                                        <td className={`px-3 py-3 align-top text-gray-600 ${WIDE_COL}`}>{it.unit || '—'}</td>
                                        <td className="px-3 py-3 align-top text-right tabular-nums text-gray-900">
                                            {formatMoney(it.price)}
                                            <div className={`${COMPACT_LINE} mt-0.5 text-xs text-gray-500 whitespace-nowrap`}>
                                                {typeof it.cost === 'number' ? `cost ${formatMoney(it.cost)}${typeof it.markupPct === 'number' ? ` · ${formatPct(it.markupPct)}` : ''}` : 'no cost'}
                                            </div>
                                        </td>
                                        <td className={`px-3 py-3 align-top text-right tabular-nums text-gray-600 ${WIDE_COL}`}>{formatMoney(it.cost)}</td>
                                        <td className={`px-3 py-3 align-top text-right tabular-nums text-gray-600 ${WIDE_COL}`}>{formatPct(it.markupPct)}</td>
                                        <td className="px-4 py-3 align-top text-gray-600">{it.defaultSupplierName || '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {search.nextCursor && !search.error && (
                <div className="flex justify-center">
                    <button type="button" className={BTN_GHOST} disabled={search.loadingMore || busy} onClick={search.showMore}>
                        {search.loadingMore ? 'Loading…' : 'Show more'}
                    </button>
                </div>
            )}

            <p className="text-xs text-gray-500">Cost, markup and supplier are visible to the office only. Customers and the portal never see them.</p>
        </div>
    );
}
